import React from 'react';
import { Link } from '../../../RouterCompatibility';
import { createSelector } from 'reselect';
import {
    getCurrencyItemInterval,
    getConversionStatus,
    getStatus
} from '../../../../helpers/common';
import {
    getQuery
} from '../../../modules';
import helperColumns from '../../../DataComponent/helper';
import { I18nFormatter } from '../../../../utils/I18n/I18n';
import { getPath } from '../../../../utils/Router';
import TreeGoalActions from '../../../TreeGoalActions/TreeGoalActions';


const history = (state) => state.postbackHistory.list;
const postbacks = (state) => state.postbacks.list.data;
const serverStore = (state) => state.serverStorage.store;

const parseSendAttempts = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch (e) {
            return [];
        }
    }
    return [];
};

const normalizeItem = (item) => {
    // Legacy CPA-shared shape with PascalCase
    if (item && item.PostbackId !== undefined) {
        return {
            id: item.Id?.toString() || item.Id,
            postback_id: item.PostbackId,
            postback_config: {
                title: item.PostbackTitle,
                locale: item.PostbackLocale,
                currency: item.PostbackCurrency
            },
            created_at: item.CreatedAtUnix,
            created_at_tz: item.CreatedAt,
            updated_at: item.UpdatedAtUnix,
            updated_at_tz: item.UpdatedAt,
            status: item.Status,
            conversion_status: item.ConversionStatus,
            send_attempts: parseSendAttempts(item.SendAttempts),
            conversion: {
                id: item.ConversionId,
                created_at: item.ConversionDateTimeUnix,
                created_at_tz: item.ConversionDateTime,
                track: {
                    created_at: item.TrackDateTimeUnix,
                    created_at_tz: item.TrackDateTime,
                    request: {
                        ip: item.Ip,
                        query: {
                            utm_source: item.UtmSource,
                            utm_medium: item.UtmMedium,
                            utm_campaign: item.UtmCampaign,
                            utm_term: item.UtmTerm,
                            utm_content: item.UtmContent,
                            param1: item.Param1,
                            param2: item.Param2,
                            param3: item.Param3,
                            param4: item.Param4
                        }
                    }
                },
                adv_cid: item.Uid,
                action: {
                    type: item.GoalActionActionType,
                    title: item.GoalActionAction
                },
                commission: {
                    sale_sum: { amount: item.SaleSumAmount, currency: item.SaleSumCurrency },
                    publisher_credit_per_lead: { amount: item.PublisherCreditPerLeadAmount + item.DynamicCpaAmount + item.DynamicCprAmount, currency: item.PublisherCreditPerLeadCurrency },
                    publisher_credit_per_sale: { amount: item.PublisherCreditPerSaleAmount - item.AdminFeePerSaleAmount, currency: item.PublisherCreditPerSaleCurrency }
                },
                contract_dynamic_admin_commission: { amount: item.AdminFeePerSaleAmount },
                goal: {
                    id: item.GoalId,
                    title: item.Goal,
                    offer: { id: item.OfferId, title: item.Offer }
                },
                offer: {
                    id: item.OfferId,
                    title: item.Offer
                }
            },
        };
    }

    // Normalized shape (lowercase)
    if (item && item.postback_id !== undefined) {
        return {
            ...item,
            send_attempts: parseSendAttempts(item.send_attempts)
        };
    }

    // Postback config list (id + config only)
    if (item && item.id !== undefined && item.config) {
        return {
            id: item.id,
            postback_id: item.id,
            postback_config: {
                title: item.config.title,
                locale: item.config.locale,
                currency: item.config.currency
            },
            created_at: null,
            updated_at: null,
            status: null,
            conversion_status: null,
            send_attempts: [],
            conversion: null
        };
    }

    return null;
};

export const mainSelector = createSelector(
    history,
    postbacks,
    (historyData = [], postbacksData = []) => {
        const list = [];

        historyData.forEach((raw) => {
            const item = normalizeItem(raw);
            if (!item) {
                return;
            }

            const title = `(${item.postback_id}) ${item.postback_config.title || ''}`;

            const conversion = item.conversion || {};
            const track = conversion.track || {};
            const trackRequest = track.request || {};
            const trackQuery = trackRequest.query || {};
            const commission = conversion.commission || {};

            // Previous version: always treated unix time at UTC, and formatted at UTC
            //     createdAt: item.created_at ? I18nFormatter.fullDateTime(+item.created_at) : '-',
            //     updatedAt: item.updated_at ? I18nFormatter.fullDateTime(+item.updated_at) : '-',
            //     createdAtConversion: conversion.created_at ? I18nFormatter.fullDateTime(+conversion.created_at) : '-',
            //     updatedAtConversion: conversion.created_at ? I18nFormatter.fullDateTime(+conversion.created_at) : '-',
            //     createdAtTrack: track.created_at ? I18nFormatter.fullDateTime(track.created_at) : '-',
            // ! ORDER MATTERS ! wrong order = non-matching data vs headers
            list.push({
                id: item.id,
                conversionPostback: <Link to={getPath('editPostback', {id: item.postback_id})}>{title}</Link>,
                createdAt: item.created_at_tz ?? '-',
                updatedAt: item.updated_at_tz ?? '-',
                status: getStatus(item.status),
                locale: item.postback_config.locale,
                currency: item.postback_config.currency,
                goalActions: conversion.goal ? <TreeGoalActions data={[{...conversion}]} nestingLevel={2} /> : '-',
                goalTitle: conversion.goal ? <TreeGoalActions data={[{...conversion}]} /> : '-',
                conversionId: conversion.id ? <Link to={getPath('conversion', {id: conversion.id})}>{conversion.id}</Link> : '-',
                createdAtConversion: conversion.created_at_tz ?? '-',
                updatedAtConversion: conversion.created_at_tz ?? '-',
                conversionStatus: getConversionStatus(item.conversion_status),
                timeFromTrackToAction: (track.created_at && conversion.created_at) ? I18nFormatter.fromDate(track.created_at, conversion.created_at) : '-',
                advCid: conversion.adv_cid,
                typeAction: conversion.action?.type,
                saleSum: commission.sale_sum ? I18nFormatter.currency(commission.sale_sum.amount, commission.sale_sum.currency, true) : '-',
                publisherCreditPerLead: commission.publisher_credit_per_lead ? I18nFormatter.currency(commission.publisher_credit_per_lead.amount, commission.publisher_credit_per_lead.currency, true) : '-',
                publisherCreditPerSale: commission.publisher_credit_per_sale ? I18nFormatter.currency(
                    commission.publisher_credit_per_sale.amount - (conversion.contract_dynamic_admin_commission?.amount || 0),
                    commission.publisher_credit_per_sale.currency,
                    true
                ) : '-',
                createdAtTrack: track.created_at_tz ?? '-',
                ip: trackRequest.ip || '-',
                ...getQuery({...trackQuery}),
                sendAttempts: getSendAttempts(item.send_attempts || [])
            });
        });

        return list;
    }
);

export const getListPostbacks = createSelector(
    postbacks,
    (postbacksData = []) => {
        return postbacksData.map(item => {
            return {
                value: item.id,
                title: item.config.title
            };
        });
    }
);

export const getHeaderName = createSelector(
    serverStore,
    (serverStore) => {
        return helperColumns.getDate('postbackHistory');
    }
);

const getSendAttempts = (list) => {
    return list.map((item) => {
        return {
            date: getCurrencyItemInterval(item.dt),
            request: item.request,
            response: item.response
        };
    });
};
